import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import { getCategoryTagStyle } from './categoryTagStyles';
import { useTheme } from '../../../../../theme/useTheme';
import TablePager from './TablePager';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';

export interface EmployeeLevelEfficiencyRow {
  location: string;
  employeeName: string;
  employeeGpid: string;
  role: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  estimatedKcph: number;
  scheduledKcph: number;
  actualKcph: number;
}

interface EmployeeLevelEfficiencyTableProps {
  title: string;
  location: string | null;
  rows: EmployeeLevelEfficiencyRow[];
  /** Full filtered row count (backend `total`), used by the pager and footnote. */
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  /** True while a page request is in flight. */
  pageLoading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Server-side sort state (backend `sortBy`/`sortDirection`) — rows arrive pre-sorted, so this
   * table no longer sorts client-side. */
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  /** Called with the clicked column's key — caller re-fetches page 0 with the new sort applied. */
  onSortChange?: (columnKey: string) => void;
}

type ColumnKey = keyof EmployeeLevelEfficiencyRow;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  width: 'wide' | 'std' | 'num';
  numeric?: boolean;
}

const CELL_WIDE = 132;
const CELL_STD = 72;
const CELL_NUM = 100;

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'location', label: 'Location', width: 'std' },
  { key: 'employeeName', label: 'Employee Name', width: 'wide' },
  { key: 'employeeGpid', label: 'GPID', width: 'std' },
  { key: 'role', label: 'Role', width: 'std' },
  { key: 'year', label: 'Year', width: 'std' },
  { key: 'period', label: 'Period', width: 'std' },
  { key: 'week', label: 'Week', width: 'std' },
  { key: 'category', label: 'Category', width: 'std' },
  { key: 'zone', label: 'Zone', width: 'std' },
  { key: 'estimatedKcph', label: 'Estimated KCPH', width: 'num', numeric: true },
  { key: 'scheduledKcph', label: 'Scheduled KCPH', width: 'num', numeric: true },
  { key: 'actualKcph', label: 'Actual KCPH', width: 'num', numeric: true },
];

const formatNum = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 1 });


const getCellWidthStyle = (width: ColumnDef['width']) => {
  if (width === 'wide') return styles.wideCell;
  if (width === 'num') return styles.numCell;
  return styles.stdCell;
};

const EmployeeLevelEfficiencyTable: React.FC<EmployeeLevelEfficiencyTableProps> = ({ title, location, rows, total, page = 0, pageSize = 25, onPageChange, pageLoading, pageSizeOptions, onPageSizeChange, sortBy, sortDirection = 'asc', onSortChange }) => {
  const isDark = useTheme().resolvedTheme === 'dark';

  const tableMinWidth = useMemo(
    () => COLUMN_DEFS.reduce((sum, col) => sum + (col.width === 'wide' ? CELL_WIDE : col.width === 'num' ? CELL_NUM : CELL_STD), 0),
    [],
  );

  return (
    <View style={styles.card}>
      <View style={styles.headerTopRow}>
        <View style={styles.titleWrap}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subtitle}>requires a single location · sortable · default Actual KCPH desc</Text>
        </View>
      </View>

      {!location ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Select a single location to view employee detail. Employee level KCPH loads once one Location is applied in the filter bar.</Text>
        </View>
      ) : (
        <>
          <Text style={styles.selectionNote}>
            Showing employees for: <Text style={styles.selectionNoteBold}>{location}</Text>
          </Text>

          <CrewingHorizontalScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scrollContent}>
            <View style={[styles.tableInner, { minWidth: tableMinWidth }]}>
              <View style={styles.headerRow}>
                {COLUMN_DEFS.map((column) => {
                  const isActive = sortBy === column.key;
                  return (
                    <TouchableOpacity
                      key={column.key}
                      style={[styles.headerCell, getCellWidthStyle(column.width)]}
                      onPress={onSortChange ? () => onSortChange(column.key) : undefined}
                      disabled={!onSortChange}
                      activeOpacity={onSortChange ? 0.7 : 1}
                    >
                      <Text style={[styles.headerCellText, isActive && styles.headerCellTextActive]} numberOfLines={2}>
                        {column.label}
                      </Text>
                      <MaterialIcons
                        name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                        size={14}
                        color={isActive ? '#034895' : '#9CA3AF'}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>

              {rows.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyText}>No employee data available for this location</Text>
                </View>
              ) : (
                <View style={styles.tableViewport}>
                  <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
                    {rows.map((row, index) => (
                      <View key={`${row.employeeGpid}-${row.week}-${index}`} style={styles.dataRow}>
                        {COLUMN_DEFS.map((column) => {
                          const rawValue = row[column.key];
                          const displayValue = column.numeric ? formatNum(Number(rawValue ?? 0)) : String(rawValue ?? '--');

                          if (column.key === 'category' && displayValue !== '--') {
                            const tagStyle = getCategoryTagStyle(displayValue, isDark);
                            return (
                              <View key={`${row.employeeGpid}-${index}-${column.key}`} style={[getCellWidthStyle(column.width), styles.categoryCell]}>
                                <View style={[styles.categoryTag, { backgroundColor: tagStyle.backgroundColor }]}>
                                  <Text style={[styles.categoryTagText, { color: tagStyle.color }]} numberOfLines={1}>
                                    {displayValue}
                                  </Text>
                                </View>
                              </View>
                            );
                          }

                          return (
                            <Text
                              key={`${row.employeeGpid}-${index}-${column.key}`}
                              style={[styles.dataCell, getCellWidthStyle(column.width)]}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {displayValue}
                            </Text>
                          );
                        })}
                      </View>
                    ))}
                  </CrewingVerticalScrollView>
                </View>
              )}
            </View>
          </CrewingHorizontalScrollView>

          <Text style={styles.footerNote}>{`${(total ?? rows.length).toLocaleString()} employees.`}</Text>
          {onPageChange && (
            <TablePager page={page} pageSize={pageSize} total={total ?? rows.length} onPageChange={onPageChange} loading={pageLoading} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    overflow: 'visible',
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  titleWrap: {
    flexDirection: 'row',
    alignItems: 'baseline',
    columnGap: 6,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    color: '#838383',
  },
  closePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  closePillText: {
    fontSize: 11,
    color: '#0B5FC6',
    fontWeight: '600',
  },
  selectionNote: {
    fontSize: 12,
    color: '#4B5563',
    marginBottom: 10,
  },
  selectionNoteBold: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  scrollContent: { flexGrow: 1 },
  tableInner: { width: '100%' },
  tableViewport: { flex: 1, minHeight: 260, maxHeight: 420 },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#FFFFFF',
    paddingVertical: 8,
  },
  headerCell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 2,
    paddingHorizontal: 6,
  },
  headerCellText: {
    flex: 1,
    fontSize: 10,
    fontFamily: FontFamily.bold,
    color: '#616161',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  headerCellTextActive: {
    color: '#034895',
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D7E5F8',
    paddingVertical: 8,
    alignItems: 'center',
  },
  dataCell: {
    paddingHorizontal: 6,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#3A3A3A',
  },
  wideCell: { flex: 2, minWidth: CELL_WIDE },
  stdCell: { flex: 1, minWidth: CELL_STD },
  numCell: { flex: 1, minWidth: CELL_NUM, textAlign: 'right' },
  categoryCell: {
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  categoryTag: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  categoryTagText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  footerNote: {
    marginTop: 10,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    lineHeight: 17,
  },
  emptyState: {
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#9CA3AF',
  },
});

export default EmployeeLevelEfficiencyTable;
